import React, { useEffect, useMemo, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";

const MONEY = new Intl.NumberFormat("es-PA", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function cents(value) {
  const n = Number(value);
  return Number.isSafeInteger(n) ? n : 0;
}

function money(value) {
  return MONEY.format(cents(value) / 100);
}

function clean(value) {
  return String(value || "").trim();
}

function businessIdOf(business) {
  return clean(
    business?.id ||
    business?.businessId ||
    business?._id ||
    business?.uid
  );
}

function businessNameOf(business) {
  return clean(
    business?.businessName ||
    business?.name ||
    business?.displayName ||
    business?.title ||
    businessIdOf(business)
  ) || "Negocio";
}

function monthLabel(value) {
  const raw = clean(value);
  if (!/^\d{4}-\d{2}$/.test(raw)) return raw || "Mes";
  const date = new Date(`${raw}-01T12:00:00.000Z`);
  return date
    .toLocaleDateString("es-PA", {
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    })
    .replace(".", "");
}

function shortDate(value) {
  const date = value ? new Date(value) : null;
  if (!date || !Number.isFinite(date.getTime())) return "—";
  return date
    .toLocaleDateString("es-PA", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    .replace(".", "");
}

function paymentKindLabel(value) {
  if (value === "membership_initial") return "Compra inicial";
  if (value === "membership_renewal") return "Renovación";
  if (value === "booking") return "Reserva / servicio";
  return "Membresía";
}

function rangeForPreset(preset) {
  const now = new Date();

  if (preset === "month") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return {
      startIso: start.toISOString(),
      endIso: end.toISOString(),
      label: now.toLocaleDateString("es-PA", { month: "long", year: "numeric" }),
    };
  }

  if (preset === "30d") {
    const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    return {
      startIso: start.toISOString(),
      endIso: now.toISOString(),
      label: "Últimos 30 días",
    };
  }

  return {
    startIso: null,
    endIso: null,
    label: "Historial financiero",
  };
}

function callableError(error) {
  return String(
    error?.message ||
    error?.details?.message ||
    error?.details ||
    "No se pudo cargar el resumen financiero."
  ).replace(/^Firebase:\s*/i, "");
}

export default function OwnerPlatformFinancialSummaryPanel({
  db,
  businesses = [],
  view = "summary",
}) {
  const [preset, setPreset] = useState("month");
  const [state, setState] = useState({
    status: "loading",
    report: null,
    diagnostics: null,
    error: "",
  });

  const range = useMemo(() => rangeForPreset(preset), [preset]);

  const businessNames = useMemo(() => {
    const map = new Map();
    (Array.isArray(businesses) ? businesses : []).forEach((business) => {
      const id = businessIdOf(business);
      if (id) map.set(id, businessNameOf(business));
    });
    return map;
  }, [businesses]);

  const businessName = (businessId) =>
    businessNames.get(clean(businessId)) || clean(businessId) || "Negocio";

  const load = async () => {
    setState((current) => ({ ...current, status: "loading", error: "" }));

    try {
      const functions = getFunctions(db?.app, "us-central1");
      const callable = httpsCallable(
        functions,
        "ownerGetPlatformFinancialReportV1",
      );

      const payload = {
        ...(range.startIso ? { startIso: range.startIso } : {}),
        ...(range.endIso ? { endIso: range.endIso } : {}),
      };

      const response = await callable(payload);
      const data = response?.data || {};
      const report = data?.report || null;

      if (!data?.ok || !report) {
        throw new Error("Loop no devolvió un reporte financiero válido.");
      }

      if (report.reportType !== "platform_membership_financials") {
        throw new Error("El tipo de reporte financiero no coincide.");
      }

      if (report.accountingBasis !== "cash_collection_wompi_approved") {
        throw new Error("La base contable del reporte no coincide.");
      }

      if (report.pAndLIncluded !== false) {
        throw new Error("Los cobros perdieron su separación del P&L.");
      }

      if (report?.totals?.settlementStatus !== "not_available_until_stage4") {
        throw new Error("El estado de liquidaciones no coincide con Stage 4.");
      }

      setState({
        status: "ready",
        report,
        diagnostics: data?.diagnostics || null,
        error: "",
      });
    } catch (error) {
      setState({
        status: "error",
        report: null,
        diagnostics: null,
        error: callableError(error),
      });
    }
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setState((current) => ({ ...current, status: "loading", error: "" }));

      try {
        const functions = getFunctions(db?.app, "us-central1");
        const callable = httpsCallable(
          functions,
          "ownerGetPlatformFinancialReportV1",
        );

        const payload = {
          ...(range.startIso ? { startIso: range.startIso } : {}),
          ...(range.endIso ? { endIso: range.endIso } : {}),
        };

        const response = await callable(payload);
        const data = response?.data || {};
        const report = data?.report || null;

        if (!data?.ok || !report) {
          throw new Error("Loop no devolvió un reporte financiero válido.");
        }

        if (report.reportType !== "platform_membership_financials") {
          throw new Error("El tipo de reporte financiero no coincide.");
        }

        if (report.accountingBasis !== "cash_collection_wompi_approved") {
          throw new Error("La base contable del reporte no coincide.");
        }

        if (report.pAndLIncluded !== false) {
          throw new Error("Los cobros perdieron su separación del P&L.");
        }

        if (report?.totals?.settlementStatus !== "not_available_until_stage4") {
          throw new Error("El estado de liquidaciones no coincide con Stage 4.");
        }

        if (!cancelled) {
          setState({
            status: "ready",
            report,
            diagnostics: data?.diagnostics || null,
            error: "",
          });
        }
      } catch (error) {
        if (!cancelled) {
          setState({
            status: "error",
            report: null,
            diagnostics: null,
            error: callableError(error),
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [db, range.startIso, range.endIso]);

  const report = state.report;
  const totals = report?.totals || {};
  const byBusiness = Array.isArray(report?.byBusiness) ? report.byBusiness : [];
  const monthly = Array.isArray(report?.monthlyTrend) ? report.monthlyTrend : [];
  const transactions = Array.isArray(report?.transactions) ? report.transactions : [];

  return (
    <section className="owner-platform-financial-summary" data-loop-owner-finance="a3h-v1">
      <style>{`
        .owner-platform-financial-summary{
          min-width:0;
          border:1px solid #dfe8eb;
          border-radius:18px;
          background:#fff;
          padding:20px;
        }
        .owner-platform-financial-summary *{box-sizing:border-box}
        .opfs-head{
          display:flex;
          justify-content:space-between;
          gap:16px;
          align-items:flex-start;
          margin-bottom:15px;
        }
        .opfs-kicker{
          display:block;
          color:#00675f;
          font-size:10px;
          font-weight:950;
          letter-spacing:.1em;
          text-transform:uppercase;
          margin-bottom:4px;
        }
        .opfs-head h2{
          margin:0;
          color:#0f172a;
          font-size:21px;
          line-height:1.15;
        }
        .opfs-head p{
          margin:6px 0 0;
          max-width:760px;
          color:#64748b;
          font-size:11px;
          line-height:1.45;
        }
        .opfs-actions{
          display:flex;
          align-items:center;
          gap:7px;
          flex-wrap:wrap;
          justify-content:flex-end;
        }
        .opfs-actions button{
          min-height:34px;
          border-radius:10px;
          border:1px solid #dbe4e8;
          background:#fff;
          color:#475569;
          padding:0 10px;
          font-size:10px;
          font-weight:850;
          cursor:pointer;
        }
        .opfs-actions button.is-active{
          border-color:#00675f;
          background:#ecfdf5;
          color:#00675f;
        }
        .opfs-actions button.opfs-refresh{
          border-color:#00675f;
          color:#00675f;
        }
        .opfs-kpis{
          display:grid;
          grid-template-columns:repeat(5,minmax(0,1fr));
          gap:9px;
        }
        .opfs-kpi{
          min-width:0;
          padding:13px;
          border:1px solid #e8eef0;
          border-radius:14px;
          background:#fbfcfd;
        }
        .opfs-kpi span{
          display:block;
          color:#64748b;
          font-size:9px;
          font-weight:850;
          margin-bottom:5px;
        }
        .opfs-kpi strong{
          display:block;
          color:#0f172a;
          font-size:19px;
          line-height:1.05;
        }
        .opfs-kpi em{
          display:block;
          margin-top:5px;
          color:#94a3b8;
          font-size:9px;
          font-style:normal;
          line-height:1.3;
        }
        .opfs-kpi.is-margin strong{
          color:#047857;
        }
        .opfs-grid{
          display:grid;
          grid-template-columns:minmax(0,1.18fr) minmax(320px,.82fr);
          gap:12px;
          margin-top:12px;
        }
        .opfs-card{
          min-width:0;
          border:1px solid #e8eef0;
          border-radius:14px;
          background:#fff;
          padding:14px;
        }
        .opfs-card-head{
          display:flex;
          justify-content:space-between;
          gap:10px;
          align-items:center;
          margin-bottom:9px;
        }
        .opfs-card-head strong{
          color:#0f172a;
          font-size:12px;
        }
        .opfs-card-head span{
          color:#94a3b8;
          font-size:9px;
          font-weight:850;
        }
        .opfs-table-wrap{
          width:100%;
          overflow:auto;
        }
        .opfs-table{
          width:100%;
          border-collapse:collapse;
          min-width:650px;
        }
        .opfs-table th{
          padding:7px 8px;
          border-bottom:1px solid #e8eef0;
          text-align:right;
          color:#94a3b8;
          font-size:8px;
          font-weight:900;
          letter-spacing:.04em;
          text-transform:uppercase;
          white-space:nowrap;
        }
        .opfs-table th:first-child,
        .opfs-table td:first-child{text-align:left}
        .opfs-table td{
          padding:9px 8px;
          border-bottom:1px solid #f1f5f9;
          text-align:right;
          color:#334155;
          font-size:10px;
          white-space:nowrap;
        }
        .opfs-table tr:last-child td{border-bottom:0}
        .opfs-business-name{
          display:block;
          max-width:240px;
          overflow:hidden;
          text-overflow:ellipsis;
          font-weight:850;
          color:#0f172a;
        }
        .opfs-months,
        .opfs-transactions{
          display:grid;
          gap:7px;
        }
        .opfs-month,
        .opfs-transaction{
          display:grid;
          gap:9px;
          align-items:center;
          padding-top:8px;
          border-top:1px solid #f1f5f9;
        }
        .opfs-month:first-child,
        .opfs-transaction:first-child{
          border-top:0;
          padding-top:0;
        }
        .opfs-month{
          grid-template-columns:minmax(80px,.75fr) repeat(4,minmax(70px,.75fr));
        }
        .opfs-transaction{
          grid-template-columns:minmax(150px,1fr) minmax(105px,.7fr) repeat(3,minmax(72px,.55fr));
        }
        .opfs-month span,
        .opfs-transaction span{
          display:block;
          color:#94a3b8;
          font-size:8px;
          font-weight:850;
          margin-bottom:2px;
        }
        .opfs-month strong,
        .opfs-transaction strong{
          display:block;
          color:#334155;
          font-size:10px;
          overflow:hidden;
          text-overflow:ellipsis;
          white-space:nowrap;
        }
        .opfs-loading,
        .opfs-error,
        .opfs-empty{
          padding:15px;
          border-radius:12px;
          font-size:11px;
          line-height:1.45;
        }
        .opfs-loading,
        .opfs-empty{
          background:#f8fafc;
          color:#64748b;
        }
        .opfs-error{
          background:#fef2f2;
          color:#991b1b;
        }
        .opfs-note{
          margin-top:12px;
          padding-top:10px;
          border-top:1px solid #eef2f7;
          color:#64748b;
          font-size:9.5px;
          line-height:1.45;
        }
        .opfs-diag{
          margin-left:4px;
          color:#94a3b8;
        }
        @media(max-width:1180px){
          .opfs-kpis{grid-template-columns:repeat(3,minmax(0,1fr))}
          .opfs-grid{grid-template-columns:1fr}
        }
        @media(max-width:720px){
          .owner-platform-financial-summary{padding:14px}
          .opfs-head{display:grid}
          .opfs-actions{justify-content:flex-start}
          .opfs-kpis{grid-template-columns:1fr 1fr}
          .opfs-month{grid-template-columns:1fr 1fr}
          .opfs-transaction{grid-template-columns:1fr 1fr}
          .opfs-mobile-hide{display:none}
        }
      `}</style>

      <div className="opfs-head">
        <div>
          <span className="opfs-kicker">Loop Finance · Super Admin</span>
          <h2>{view === "movements" ? "Movimientos del período" : "Resumen financiero de procesamiento"}</h2>
          <p>
            Cobros de membresías y reservas aprobados por Wompi, costo contractual estimado,
            fee cobrado por Loop, margen de procesamiento y monto que corresponde al negocio.
          </p>
        </div>

        <div className="opfs-actions">
          {[
            ["month", "Este mes"],
            ["30d", "30 días"],
            ["all", "Historial"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={preset === key ? "is-active" : ""}
              onClick={() => setPreset(key)}
            >
              {label}
            </button>
          ))}
          <button
            type="button"
            className="opfs-refresh"
            onClick={load}
            disabled={state.status === "loading"}
          >
            {state.status === "loading" ? "Actualizando…" : "Actualizar"}
          </button>
        </div>
      </div>

      {state.status === "error" ? (
        <div className="opfs-error">{state.error}</div>
      ) : state.status === "loading" && !report ? (
        <div className="opfs-loading">Cargando resumen financiero de Loop…</div>
      ) : report ? (
        <>
          <div className="opfs-kpis">
            <article className="opfs-kpi">
              <span>Cobrado bruto</span>
              <strong>{money(totals.grossCollectedCents)}</strong>
              <em>{cents(totals.transactionCount)} transacción{cents(totals.transactionCount) === 1 ? "" : "es"} aprobada{cents(totals.transactionCount) === 1 ? "" : "s"}</em>
            </article>

            <article className="opfs-kpi">
              <span>Costo Wompi estimado</span>
              <strong>{money(totals.estimatedProcessorCostCents)}</strong>
              <em>Costo contractual interno de procesamiento</em>
            </article>

            <article className="opfs-kpi">
              <span>Fee cobrado por Loop</span>
              <strong>{money(totals.merchantFeeCents)}</strong>
              <em>Cargo de procesamiento al negocio</em>
            </article>

            <article className="opfs-kpi is-margin">
              <span>Margen Loop estimado</span>
              <strong>{money(totals.estimatedLoopProcessingMarginCents)}</strong>
              <em>Fee Loop menos costo Wompi estimado</em>
            </article>

            <article className="opfs-kpi">
              <span>Monto a liquidar</span>
              <strong>{money(totals.merchantPayableCents)}</strong>
              <em>Bruto menos fee cobrado al negocio</em>
            </article>
          </div>

          <div className="opfs-grid">
            <article className="opfs-card">
              <div className="opfs-card-head">
                <strong>Por negocio</strong>
                <span>{byBusiness.length} negocio{byBusiness.length === 1 ? "" : "s"}</span>
              </div>

              {byBusiness.length ? (
                <div className="opfs-table-wrap">
                  <table className="opfs-table">
                    <thead>
                      <tr>
                        <th>Negocio</th>
                        <th>Tx</th>
                        <th>Bruto</th>
                        <th>Wompi est.</th>
                        <th>Fee Loop</th>
                        <th>Margen Loop</th>
                        <th>A liquidar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byBusiness.map((row) => (
                        <tr key={row.businessId}>
                          <td>
                            <span className="opfs-business-name" title={businessName(row.businessId)}>
                              {businessName(row.businessId)}
                            </span>
                          </td>
                          <td>{cents(row.transactionCount)}</td>
                          <td>{money(row.grossCollectedCents)}</td>
                          <td>{money(row.estimatedProcessorCostCents)}</td>
                          <td>{money(row.merchantFeeCents)}</td>
                          <td>{money(row.estimatedLoopProcessingMarginCents)}</td>
                          <td>{money(row.merchantPayableCents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="opfs-empty">No hay cobros registrados en {range.label.toLowerCase()}.</div>
              )}
            </article>

            <article className="opfs-card">
              <div className="opfs-card-head">
                <strong>Evolución mensual</strong>
                <span>{monthly.length} mes{monthly.length === 1 ? "" : "es"}</span>
              </div>

              {monthly.length ? (
                <div className="opfs-months">
                  {monthly.slice().reverse().slice(0, 12).map((row) => (
                    <div className="opfs-month" key={row.month}>
                      <div>
                        <span>MES</span>
                        <strong>{monthLabel(row.month)}</strong>
                      </div>
                      <div>
                        <span>BRUTO</span>
                        <strong>{money(row.grossCollectedCents)}</strong>
                      </div>
                      <div className="opfs-mobile-hide">
                        <span>WOMPI EST.</span>
                        <strong>{money(row.estimatedProcessorCostCents)}</strong>
                      </div>
                      <div className="opfs-mobile-hide">
                        <span>MARGEN LOOP</span>
                        <strong>{money(row.estimatedLoopProcessingMarginCents)}</strong>
                      </div>
                      <div>
                        <span>A LIQUIDAR</span>
                        <strong>{money(row.amountToSettleCents)}</strong>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="opfs-empty">Sin evolución mensual para este rango.</div>
              )}
            </article>
          </div>

          <article className="opfs-card opfs-movements-panel" style={{ marginTop: 12 }}>
            <div className="opfs-card-head">
              <strong>Transacciones del reporte</strong>
              <span>{transactions.length}</span>
            </div>

            {transactions.length ? (
              <div className="opfs-transactions">
                {transactions.map((row) => (
                  <div className="opfs-transaction" key={row.providerTransactionId}>
                    <div>
                      <span>{paymentKindLabel(row.paymentKind)}</span>
                      <strong>{businessName(row.businessId)} · {shortDate(row.occurredAtIso)}</strong>
                    </div>
                    <div>
                      <span>BRUTO</span>
                      <strong>{money(row.grossCollectedCents)}</strong>
                    </div>
                    <div className="opfs-mobile-hide">
                      <span>WOMPI EST.</span>
                      <strong>{money(row.estimatedProcessorCostCents)}</strong>
                    </div>
                    <div className="opfs-mobile-hide">
                      <span>MARGEN LOOP</span>
                      <strong>{money(row.estimatedLoopProcessingMarginCents)}</strong>
                    </div>
                    <div>
                      <span>A LIQUIDAR</span>
                      <strong>{money(row.amountToSettleCents)}</strong>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="opfs-empty">Sin transacciones aprobadas para este rango.</div>
            )}
          </article>

          <div className="opfs-note">
            Base: cobros de membresías y reservas aprobados por Wompi · {range.label}.
            El costo Wompi es contractual/estimado hasta conciliación real.
            “Monto a liquidar” no significa “liquidado”; settled/pending se incorporarán en Stage 4.
            Este resumen de cobros no sustituye las ventas reconocidas ni confirma transferencias bancarias.
            {state.diagnostics?.scannedLedgerDocs != null ? (
              <span className="opfs-diag">
                Ledger leído: {state.diagnostics.scannedLedgerDocs}/{state.diagnostics.maxDocs}.
              </span>
            ) : null}
          </div>
        </>
      ) : (
        <div className="opfs-empty">Todavía no hay datos financieros para mostrar.</div>
      )}
    </section>
  );
}
